import { TZDate } from "@date-fns/tz";
import { format } from "date-fns";
import { $TradingLimits_Output } from "@/client-admin/schemas.gen";
import { LimitCheckResult, TradingLimits_Output } from "@/client-admin/types.gen";

export type TradingLimitField = keyof TradingLimits_Output;

type CheckName = LimitCheckResult["limit_name"];

/** Limit fields in backend order; labels are the backend field titles. */
export const TRADING_LIMIT_FIELDS = Object.keys($TradingLimits_Output.properties) as TradingLimitField[];

export const tradingLimitLabel = (field: TradingLimitField): string => $TradingLimits_Output.properties[field].title;

/** Limits grouped by what they constrain, in display order. */
export const TRADING_LIMIT_GROUPS: { name: string; fields: TradingLimitField[] }[] = [
  { name: "Volume", fields: ["max_trade_volume_mwh_per_ptu", "max_hourly_volume_mwh", "max_daily_volume_mwh"] },
  { name: "Notional", fields: ["day_notional_cap_eur"] },
  { name: "Limit price vs Day Ahead", fields: ["price_delta_eur", "price_delta_percent"] },
  { name: "Timing", fields: ["minutes_to_closed_id"] },
];

export const TRADING_LIMIT_UNITS: Record<TradingLimitField, string> = {
  max_trade_volume_mwh_per_ptu: "MWh",
  max_hourly_volume_mwh: "MWh",
  max_daily_volume_mwh: "MWh",
  day_notional_cap_eur: "EUR",
  price_delta_eur: "EUR/MWh",
  price_delta_percent: "%",
  minutes_to_closed_id: "min",
};

/** Limits that can be turned off for a day (null). */
export const OPTIONAL_TRADING_LIMIT_FIELDS: TradingLimitField[] = ["price_delta_eur", "price_delta_percent"];

/** Limit fields each check reads; the price check uses the wider of its two bands. */
const CHECK_FIELDS: Record<CheckName, TradingLimitField[]> = {
  MaxTradeVolumeMwhPerPtu: ["max_trade_volume_mwh_per_ptu"],
  MaxHourlyVolumeMwh: ["max_hourly_volume_mwh"],
  MaxDailyVolumeMwh: ["max_daily_volume_mwh"],
  DayNotionalCapEur: ["day_notional_cap_eur"],
  PriceDelta: ["price_delta_eur", "price_delta_percent"],
};

export const checkFields = (checkName: CheckName): TradingLimitField[] => CHECK_FIELDS[checkName];

export const checkLabel = (checkName: CheckName): string => CHECK_FIELDS[checkName].map(tradingLimitLabel).join(" / ");

/** What a check measures per row: PTUs, clock hours or the whole day. */
export const BREACH_UNITS: Record<CheckName, { column: string; one: string; counted: string }> = {
  MaxTradeVolumeMwhPerPtu: { column: "PTU", one: "PTU", counted: "PTUs" },
  MaxHourlyVolumeMwh: { column: "Hour", one: "hour", counted: "hours" },
  MaxDailyVolumeMwh: { column: "Day", one: "day", counted: "days" },
  DayNotionalCapEur: { column: "Day", one: "day", counted: "days" },
  PriceDelta: { column: "PTU", one: "PTU", counted: "PTUs" },
};

/** Unit of a check's actual and limit. The price check measures the distance
 * to the Day Ahead price in EUR/MWh; its limit is already in EUR/MWh. */
const CHECK_UNITS: Record<CheckName, { unit: string; decimals: number }> = {
  MaxTradeVolumeMwhPerPtu: { unit: "MWh", decimals: 3 },
  MaxHourlyVolumeMwh: { unit: "MWh", decimals: 3 },
  MaxDailyVolumeMwh: { unit: "MWh", decimals: 3 },
  DayNotionalCapEur: { unit: "EUR", decimals: 2 },
  PriceDelta: { unit: "EUR/MWh", decimals: 2 },
};

export const formatCheckNumber = (checkName: CheckName, value: string): string =>
  Number(value).toLocaleString("en-GB", {
    minimumFractionDigits: CHECK_UNITS[checkName].decimals,
    maximumFractionDigits: CHECK_UNITS[checkName].decimals,
  });

export const checkUnit = (checkName: CheckName): string => CHECK_UNITS[checkName].unit;

export const formatCheckValue = (checkName: CheckName, value: string): string =>
  `${formatCheckNumber(checkName, value)} ${checkUnit(checkName)}`;

export const formatTradingLimit = (field: TradingLimitField, value: string | number | null): string => {
  if (value === null) return "Off";
  if (field === "minutes_to_closed_id" || Number.isNaN(Number(value))) return String(value);
  return Number(value).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Same limit value, whatever its scale: "150" equals "150.00"; null is off. */
export const sameTradingLimit = (a: string | number | null, b: string | number | null): boolean =>
  a === null || b === null ? a === b : Number(a) === Number(b);

/** "10:00:00", "10:15:00" → "10:00–10:15" */
export const formatPeriod = (start: string, end: string): string => `${start.slice(0, 5)}–${end.slice(0, 5)}`;

/** Merge periods sorted by start where one ends as the next starts:
 * 11:45–12:00, 12:00–12:15, 12:45–13:00 → 11:45–12:15, 12:45–13:00 */
export const mergeConsecutivePeriods = (periods: { start: string; end: string }[]): { start: string; end: string }[] =>
  periods.reduce<{ start: string; end: string }[]>((merged, period) => {
    const last = merged[merged.length - 1];
    if (last && last.end === period.start) {
      return [...merged.slice(0, -1), { start: last.start, end: period.end }];
    }
    return [...merged, period];
  }, []);

export const todayInAmsterdam = (): string => format(new TZDate(new Date(), "Europe/Amsterdam"), "yyyy-MM-dd");

export const formatAmsterdamDateTime = (timestamp: string): string =>
  format(new TZDate(timestamp, "Europe/Amsterdam"), "yyyy-MM-dd HH:mm");
